import type { ApplicationContextManager } from './checkpointHelper/static/appfactory3';
import { navigateTo } from './views';
import { showToast } from './toast';
import { csrf } from './csrf';
import { getCurrentUserId } from './checkpointHelper/auth/context';
import { PAGE_SIZES, type DocumentMeta } from './editor';

interface SharedDocumentMeta {
    id: number;
    name: string;
    size: number;
    updated_at: string;
    permission: 'view' | 'edit';
}

/** Appends the Checkpoint user id every document request is scoped by (see DocumentController::resolveReferenceNum). */
function withUserId(url: string): string {
    const userId = getCurrentUserId();
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}user_id=${encodeURIComponent(String(userId ?? ''))}`;
}

function $<T extends HTMLElement>(sel: string): T {
    const el = document.querySelector<T>(sel);
    if (!el) throw new Error(`missing element ${sel}`);
    return el;
}

export function initDashboard(app: ApplicationContextManager): void {
    $('#new-letter').addEventListener('click', () => navigateTo(app, 'editor', { action: 'new', size: PAGE_SIZES.letter }));
    $('#new-a4').addEventListener('click', () => navigateTo(app, 'editor', { action: 'new', size: PAGE_SIZES.a4 }));

    const pdfFileInput = $<HTMLInputElement>('#pdf-file-input');
    $('#open-file').addEventListener('click', () => {
        pdfFileInput.value = '';
        pdfFileInput.click();
    });

    pdfFileInput.addEventListener('change', async () => {
        const file = pdfFileInput.files?.[0];
        if (!file) return;
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            navigateTo(app, 'editor', { action: 'open-bytes', bytes, name: file.name, docId: null });
        } catch (err) {
            console.error(err);
            showToast('Could not open that PDF');
        }
    });

    void loadDocumentList(app);
    void loadSharedWithMeList(app);
}

async function loadDocumentList(app: ApplicationContextManager): Promise<void> {
    const host = $<HTMLDivElement>('#doc-list');
    try {
        const res = await fetch(withUserId('/api/documents'), { headers: { Accept: 'application/json' } });
        const docs = (await res.json()) as DocumentMeta[];
        if (docs.length === 0) {
            host.innerHTML = '<div class="text-secondary text-center py-3 small">No saved documents yet.</div>';
            return;
        }
        host.innerHTML = '';
        for (const doc of docs) {
            const row = document.createElement('div');
            row.className = 'list-group-item d-flex align-items-center gap-2 px-2';
            const kb = Math.max(1, Math.round(doc.size / 1024));
            row.innerHTML =
                `<span class="fs-3">📕</span>` +
                `<button class="doc-open flex-fill text-truncate"></button>` +
                `<span class="text-secondary small flex-none">${kb} KB</span>` +
                `<button class="doc-del flex-none" title="Delete">✕</button>`;
            const openBtn = row.querySelector<HTMLButtonElement>('.doc-open')!;
            openBtn.textContent = doc.name;
            openBtn.addEventListener('click', async () => {
                const fileRes = await fetch(withUserId(`/api/documents/${doc.id}/file`));
                if (!fileRes.ok) {
                    showToast('Could not load document');
                    return;
                }
                const bytes = new Uint8Array(await fileRes.arrayBuffer());
                navigateTo(app, 'editor', { action: 'open-bytes', bytes, name: doc.name, docId: doc.id });
            });
            row.querySelector<HTMLButtonElement>('.doc-del')!.addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!confirm(`Delete "${doc.name}"?`)) return;
                await fetch(withUserId(`/api/documents/${doc.id}`), { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf } });
                void loadDocumentList(app);
            });
            host.appendChild(row);
        }
    } catch {
        host.innerHTML = '<div class="text-danger text-center py-3 small">Could not load documents.</div>';
    }
}

async function loadSharedWithMeList(app: ApplicationContextManager): Promise<void> {
    const host = $<HTMLDivElement>('#shared-doc-list');
    try {
        const res = await fetch(withUserId('/api/shared-with-me'), { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        const docs = (await res.json()) as SharedDocumentMeta[];
        if (docs.length === 0) {
            host.innerHTML = '<div class="text-secondary text-center py-3 small">Nothing has been shared with you yet.</div>';
            return;
        }
        host.innerHTML = '';
        for (const doc of docs) {
            const row = document.createElement('div');
            row.className = 'list-group-item d-flex align-items-center gap-2 px-2';
            const kb = Math.max(1, Math.round(doc.size / 1024));
            row.innerHTML =
                `<span class="fs-3">📗</span>` +
                `<button class="doc-open flex-fill text-truncate"></button>` +
                `<span class="badge bg-${doc.permission === 'edit' ? 'primary' : 'secondary'}-lt flex-none">${doc.permission}</span>` +
                `<span class="text-secondary small flex-none">${kb} KB</span>`;
            const openBtn = row.querySelector<HTMLButtonElement>('.doc-open')!;
            openBtn.textContent = doc.name;
            openBtn.addEventListener('click', async () => {
                const fileRes = await fetch(withUserId(`/api/documents/${doc.id}/file`));
                if (!fileRes.ok) {
                    showToast('Could not load document');
                    return;
                }
                const bytes = new Uint8Array(await fileRes.arrayBuffer());
                navigateTo(app, 'editor', {
                    action: 'open-bytes',
                    bytes,
                    name: doc.name,
                    docId: doc.id,
                    sharePermission: doc.permission,
                });
            });
            host.appendChild(row);
        }
    } catch {
        host.innerHTML = '<div class="text-danger text-center py-3 small">Could not load shared documents.</div>';
    }
}
